import React, { useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import styled, { css } from 'styled-components'

import {
  routes,
  useGetWorkspaces,
  useUserAuthorizations,
} from '@sketch/modules-common'
import { InboxFilters } from '@sketch/user'
import { ButtonUnstyled, Tooltip } from '@sketch/components'
import { WorkspaceMinimalFragment } from '@sketch/gql-types'

import { ReactComponent as ChevronLeft } from '@sketch/icons/chevron-left-24'

import UserNavbarSection from '../../../user/components/UserNavbarSection'

import { SidebarWrapper, WorkspaceSelectorWrapper } from './Sidebar.styles'

const BackButton = styled(ButtonUnstyled)(
  ({ theme }) => css`
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: ${theme.colors.background.tertiary.C};
    color: ${theme.colors.foreground.secondary.D};

    &:hover {
      background-color: ${theme.colors.background.tertiary.B};
      color: ${theme.colors.foreground.secondary.A};
    }
  `
)

const BackIcon = styled(ChevronLeft)`
  width: 24px;
  height: 24px;
`

interface InboxSidebarProps {
  workspace: WorkspaceMinimalFragment
}

export const InboxSidebar = ({ workspace }: InboxSidebarProps) => {
  const { workspaces } = useGetWorkspaces()
  const { hasAccessToWorkspace } = useUserAuthorizations()

  const accessibleWorkspaces = useMemo(
    () =>
      (workspaces ?? []).filter(w =>
        hasAccessToWorkspace({
          identifier: w.identifier,
          customer: w.customer,
          userRole: w.userRole,
        })
      ),
    [workspaces, hasAccessToWorkspace]
  )

  const isPersonalWorkspace = workspace.type === 'PERSONAL'
  const backRoute = isPersonalWorkspace
    ? routes.ALL_DOCUMENTS.create({})
    : routes.WORKSPACE_SHARES.create({ workspaceId: workspace.identifier })

  const backLabel = isPersonalWorkspace
    ? 'Back to Documents'
    : `Back to ${workspace.name}`

  return (
    <SidebarWrapper>
      <WorkspaceSelectorWrapper>
        <Tooltip content={backLabel} placement="bottom" disableWhenTouchDevice>
          <BackButton as={NavLink} to={backRoute} aria-label={backLabel}>
            <BackIcon />
          </BackButton>
        </Tooltip>

        <UserNavbarSection workspace={workspace} />
      </WorkspaceSelectorWrapper>

      <InboxFilters workspaces={accessibleWorkspaces} />
    </SidebarWrapper>
  )
}
